/**
 * JSON Editor with Syntax Highlighting
 */

import React, { useRef, useCallback } from 'react';

// ============================================================================
// JSON Syntax Highlighting
// ============================================================================

const JSON_COLORS = {
    key: '#9cdcfe',      // Light blue for keys
    string: '#ce9178',   // Orange/brown for strings
    number: '#b5cea8',   // Light green for numbers
    boolean: '#569cd6',  // Blue for booleans
    null: '#569cd6',     // Blue for null
    punctuation: '#d4d4d4', // Gray for brackets, colons, commas
};

const SyntaxHighlightedJson: React.FC<{ json: string }> = ({ json }) => {
    const highlightJson = (text: string): React.ReactNode[] => {
        const result: React.ReactNode[] = [];
        let i = 0;
        let keyIndex = 0;

        const pushSpan = (content: string, color: string) => {
            result.push(
                <span key={keyIndex++} style={{ color }}>
                    {content}
                </span>
            );
        };

        while (i < text.length) {
            const char = text[i];

            // Whitespace
            if (/\s/.test(char)) {
                let ws = '';
                while (i < text.length && /\s/.test(text[i])) {
                    ws += text[i];
                    i++;
                }
                result.push(<span key={keyIndex++}>{ws}</span>);
                continue;
            }

            // String (key or value)
            if (char === '"') {
                let str = '"';
                i++;
                while (i < text.length && text[i] !== '"') {
                    if (text[i] === '\\' && i + 1 < text.length) {
                        str += text[i] + text[i + 1];
                        i += 2;
                    } else {
                        str += text[i];
                        i++;
                    }
                }
                if (i < text.length) {
                    str += '"';
                    i++;
                }

                // Check if this is a key (followed by colon)
                let j = i;
                while (j < text.length && /\s/.test(text[j])) j++;
                const isKey = text[j] === ':';

                pushSpan(str, isKey ? JSON_COLORS.key : JSON_COLORS.string);
                continue;
            }

            // Number
            if (/[-\d]/.test(char)) {
                let num = '';
                while (i < text.length && /[-\d.eE+]/.test(text[i])) {
                    num += text[i];
                    i++;
                }
                pushSpan(num, JSON_COLORS.number);
                continue;
            }

            // Boolean or null
            if (text.slice(i, i + 4) === 'true') {
                pushSpan('true', JSON_COLORS.boolean);
                i += 4;
                continue;
            }
            if (text.slice(i, i + 5) === 'false') {
                pushSpan('false', JSON_COLORS.boolean);
                i += 5;
                continue;
            }
            if (text.slice(i, i + 4) === 'null') {
                pushSpan('null', JSON_COLORS.null);
                i += 4;
                continue;
            }

            // Punctuation
            if (/[{}[\]:,]/.test(char)) {
                pushSpan(char, JSON_COLORS.punctuation);
                i++;
                continue;
            }

            // Unknown character
            result.push(<span key={keyIndex++}>{char}</span>);
            i++;
        }

        return result;
    };

    return <>{highlightJson(json)}</>;
};

// ============================================================================
// JSON Editor Component
// ============================================================================

export interface JsonEditorProps {
    value: string;
    onChange: (value: string) => void;
    error?: string | null;
    minHeight?: string;
}

// Shared text styles to ensure exact alignment between textarea and highlight layer
const textStyles: React.CSSProperties = {
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    fontSize: '12px',
    lineHeight: '18px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    letterSpacing: 'normal',
    tabSize: 2,
};

export const JsonEditor: React.FC<JsonEditorProps> = ({
    value,
    onChange,
    error,
    minHeight = '200px',
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const highlightRef = useRef<HTMLDivElement>(null);

    // Sync scroll positions
    const handleScroll = useCallback(() => {
        if (textareaRef.current && highlightRef.current) {
            highlightRef.current.scrollTop = textareaRef.current.scrollTop;
            highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
        }
    }, []);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {error && (
                <div style={{
                    padding: '8px 12px',
                    marginBottom: '8px',
                    backgroundColor: '#ff5252',
                    color: '#fff',
                    borderRadius: '4px',
                    fontSize: '12px',
                }}>
                    {error}
                </div>
            )}
            <div style={{
                flex: 1,
                minHeight,
                position: 'relative',
                backgroundColor: '#1e1e1e',
                border: '1px solid #444',
                borderRadius: '4px',
                overflow: 'hidden',
            }}>
                {/* Syntax highlighted layer (behind) */}
                <div
                    ref={highlightRef}
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        padding: '12px',
                        margin: 0,
                        border: 'none',
                        pointerEvents: 'none',
                        overflow: 'auto',
                        color: '#d4d4d4',
                        ...textStyles,
                    }}
                >
                    <SyntaxHighlightedJson json={value} />
                </div>
                {/* Editable textarea layer (front, transparent text) */}
                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onScroll={handleScroll}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoComplete="off"
                    autoCorrect="off"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        boxSizing: 'border-box',
                        padding: '12px',
                        margin: 0,
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: 'transparent',
                        caretColor: '#fff',
                        resize: 'none',
                        outline: 'none',
                        overflow: 'auto',
                        ...textStyles,
                    }}
                />
            </div>
        </div>
    );
};
